/**
 * ч.1327 — випадне вікно поля (список SearchSelect, календар DatePopover) у порталі `body`.
 *
 * Раніше вікно було `position: absolute` всередині поля. Таблична частина документа тепер
 * прокручується вбік (`TchTable`, контейнер з `overflow-x: auto`), а такий контейнер обрізає
 * все, що виходить за його межі: список номенклатури в останньому рядку ховався б під край
 * таблиці. Так само обрізали вікно модальні вікна з прокруткою. NiceSelect давно в порталі
 * (ч.965) — тут той самий підхід для решти полів:
 *   • вікно `position: fixed` під полем (або над ним, якщо знизу тісно);
 *   • не виходить за правий край екрана;
 *   • їде за полем при прокрутці будь-якого контейнера і при зміні розміру вікна;
 *   • клік поза полем І поза вікном — `onClose` (вікно вже не всередині поля, тому
 *     перевірка «клік усередині кореня поля» сама по собі закривала б його при кожному кліку).
 * Події React із порталу і далі спливають деревом компонентів (onMouseDown, onKeyDown).
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type React from 'react';

export const POPUP_Z = 1300;   // вище модальних вікон (1000–1200), де теж бувають такі поля

interface Pos { top: number; left: number; width: number; up: boolean }

export function useAnchoredPopup(
  open: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  popRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  { estHeight = 320, gap = 2 }: { estHeight?: number; gap?: number } = {},
): { style: React.CSSProperties; anchorWidth: number } {
  const [pos, setPos] = useState<Pos>({ top: 0, left: 0, width: 0, up: false });

  const place = useCallback(() => {
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < estHeight && r.top > below;
    let left = r.left;
    const popW = popRef.current?.offsetWidth ?? 0;
    if (popW && left + popW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - popW);
    const next = { top: up ? r.top - gap : r.bottom + gap, left, width: r.width, up };
    setPos((p) => (p.top === next.top && p.left === next.left && p.width === next.width && p.up === next.up
      ? p : next));
  }, [anchorRef, popRef, estHeight, gap]);

  // Двічі: спершу під полем, потім — коли вікно намальоване і відома його ширина.
  useLayoutEffect(() => { if (open) { place(); requestAnimationFrame(place); } }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || popRef.current?.contains(t)) return;
      onClose();
    };
    const onMove = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, onClose, place, anchorRef, popRef]);

  return {
    style: {
      position: 'fixed', zIndex: POPUP_Z, left: pos.left,
      top: pos.up ? undefined : pos.top,
      bottom: pos.up ? window.innerHeight - pos.top : undefined,
    },
    anchorWidth: pos.width,
  };
}

export default useAnchoredPopup;
